import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Search, ShoppingBag, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useShopify } from '../../contexts/ShopifyContext';
import { SINGLE_PRODUCT_MODE } from '../../config/featureFlags';
import styles from './Header.module.css';

const Header: React.FC = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { openCart, checkout } = useShopify();

  const cartCount = checkout?.lineItems?.reduce(
    (total: number, item: any) => total + (item?.quantity || 0),
    0
  ) || 0;

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header className={`${styles.header} ${isScrolled ? styles.scrolled : ''}`}>
        <div className={styles.container}>
          <button
            className={styles.menuButton}
            onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <Link to="/home" className={styles.logo}>
            <img src="/KarlanoBack.png" alt="Karlota" className={styles.logoImage} />
          </Link>

          <nav className={styles.desktopNav}>
            <Link to="/home">Home</Link>
            <Link to="/shop">Shop</Link>
            <Link to="/about">About</Link>
          </nav>

          <div className={styles.actions}>
            <button
              className={styles.actionButton}
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              aria-label="Search"
            >
              <Search size={20} />
            </button>

            {!SINGLE_PRODUCT_MODE && (
              <Link to="/account" className={styles.actionButton}>
                <User size={20} />
              </Link>
            )}

            <button
              className={styles.actionButton}
              onClick={openCart}
              aria-label="Shopping bag"
            >
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span className={styles.badge}>{cartCount}</span>
              )}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isSearchOpen && (
            <motion.div
              initial={{ maxHeight: 0, opacity: 0 }}
              animate={{ maxHeight: 80, opacity: 1 }}
              exit={{ maxHeight: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              className={styles.searchBar}
            >
              <input
                type="search"
                placeholder="Search products..."
                className={styles.searchInput}
                autoFocus
              />
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className={styles.overlay}
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.nav
              initial={{ opacity: 0, transform: 'translateX(-100%)' }}
              animate={{ opacity: 1, transform: 'translateX(0)' }}
              exit={{ opacity: 0, transform: 'translateX(-100%)' }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              className={styles.mobileMenu}
            >
              <div className={styles.mobileMenuLinks}>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setIsSearchOpen(true);
                  }}
                  className={styles.mobileSearchButton}
                >
                  <Search size={18} />
                  <span>Search</span>
                </button>
                <Link to="/home" onClick={() => setMobileMenuOpen(false)}>
                  Home
                </Link>
                <Link to="/shop" onClick={() => setMobileMenuOpen(false)}>
                  Shop
                </Link>
                <Link to="/about" onClick={() => setMobileMenuOpen(false)}>
                  About
                </Link>
              </div>
              <div className={styles.mobileMenuFooter}>
                <Link to="/size-guide">Size Guide</Link>
                <Link to="/contact">Contact</Link>
                <Link to="/about">About</Link>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;